import type { IStringifyOptions } from 'qs';

import type { IdCampaignDto } from '@/services/ws-orders/src/main/resources/rest-api';
import { PostCampaignStatus } from '@/services/ws-post/src/main/resources/rest-api';
import { FileSize } from '@/js/libs/file-size';

import { LanguageKey } from './types';
import { getArrayElement } from './utils/getArrayElement';

export const DEV_MODE = import.meta.env.MODE === 'development';

export const DEFAULT_ERROR = 'Unknown error';
export const ORDER = {
  ASC: 'ASC',
  DESC: 'DESC',
};
export const DATE_PARAMETER_FORMAT = 'YYYY-MM-DD';
export const DATE_PARAMETER_FORMAT_REG_EXP = /^\d{4}-\d{2}-\d{2}$/;
export const DATE_FORMAT = 'DD.MM.YYYY';
export const DATE_FORMAT_SHORT = 'DD.MM.YY';
export const DATE_FORMAT_LONG = 'DD MMM YYYY';
export const DATE_FORMAT_V3 = 'dd.MM.yyyy';
export const TIME_FORMAT = 'HH:mm';
export const DATE_TIME_FORMAT = 'DD.MM.YYYY HH:mm';
export const DATE_TIME_FORMAT_V3 = 'dd.MM.yyyy HH:mm';
export const DATE_TIME_SECONDS_FORMAT = 'DD.MM.YYYY HH:mm:ss';

export const ALL = 'ALL';
export const OFFERED = 'OFFERED';
export const DRAFTS = 'DRAFTS';
export const APPROVALS = 'APPROVALS';
export const PREPAYMENT = 'PREPAYMENT';
export const BOOKED = 'BOOKED';
export const RUNNING = 'RUNNING';
export const ARCHIVED = 'ARCHIVED';

export const COMPANY_ID_ORF = '1655';

export const CAMPAIGN_TYPES = [ALL, OFFERED, DRAFTS, APPROVALS, PREPAYMENT, BOOKED, RUNNING, ARCHIVED];

export const POST_CAMPAIGN_STATUSES: PostCampaignStatus[] = [
  PostCampaignStatus.ALL,
  PostCampaignStatus.ARCHIVED,
  PostCampaignStatus.BOOKED,
  PostCampaignStatus.OFFERED,
  PostCampaignStatus.RUNNING,
  PostCampaignStatus.UNFINISHED,
];

export const SERVICE_DASHBOARD_CAMPAIGN_LINK = (campaignId: IdCampaignDto): string => `/orders/campaigns/ALL/${campaignId}`;

export const MINUTE = 60000;

export const MAX_INT = 2147483647;

export const ICONS = {
  ROCKET: 0x1f680,
};

export const cantons = [
  'BE',
  'GL',
  'AG',
  'SZ',
  'AI',
  'OW',
  'LU',
  'NW',
  'BL',
  'GR',
  'JU',
  'FR',
  'ZG',
  'VD',
  'ZH',
  'AR',
  'BS',
  'TG',
  'SG',
  'SH',
  'TI',
  'NE',
  'SO',
  'UR',
  'VS',
  'GE',
];

export const CAMPAIGN_INVOICE_LABEL_CHARGED = 'CHARGED';
export const CAMPAIGN_INVOICE_LABEL_PAID = 'PAID';
export const CAMPAIGN_INVOICE_LABEL_FIRST_REMINDER = 'FIRST_REMINDER';
export const CAMPAIGN_INVOICE_LABEL_SECOND_REMINDER = 'SECOND_REMINDER';
export const CAMPAIGN_INVOICE_LABEL_THIRD_REMINDER = 'THIRD_REMINDER';
export const CAMPAIGN_INVOICE_LABEL_PERSONAL_CALL_REMINDER = 'PERSONAL_CALL_REMINDER';
export const CAMPAIGN_INVOICE_LABEL_PERSONAL_CALL_REQUESTED_REMINDER = 'PERSONAL_CALL_REQUESTED_REMINDER';
export const CAMPAIGN_INVOICE_LABEL_COLLECTION_REMINDER = 'COLLECTION_REMINDER';
export const CAMPAIGN_INVOICE_LABEL_BAD_DEBT = 'BAD_DEBT';

export const CAMPAIGN_TYPE_PR = 'PR';

export const APP_NEXUS_LINE_ITEM_URL_PREFIX = 'https://monetize.xandr.com/smw/line-items?line_item_id=';
export const APP_GAM_LINE_ITEM_URL_PREFIX = 'https://admanager.google.com/96628199#delivery/line_item/detail/line_item_id=';
export const MOBILE_HEADER_HEIGHT = 60;
export const TO_TOP_ARROW_OFFSET = 1000;
export const GENERATE_NEW_ACCOUNT = 'GENERATE_NEW_ACCOUNT' as const;

export const COUNTRIES = ['CH', 'AT', 'DE'];

export const CHECK_STATUS_INTERVAL = 60000;

export const languageTitle: Record<LanguageKey, string> = {
  [LanguageKey.German]: 'language:DE',
  [LanguageKey.English]: 'language:EN',
  [LanguageKey.French]: 'language:FR',
  [LanguageKey.Italian]: 'language:IT',
};

export const LANGUAGE_KEYS = ['de', 'en', 'fr', 'it'];

export const PASSENDO_AD_SERVER_LINK_PREFIX = 'https://admin.passendo.com/stats/offer/';

export const supportedLanguages: readonly LanguageKey[] = [
  LanguageKey.German,
  LanguageKey.English,
  LanguageKey.French,
  LanguageKey.Italian,
];

export const FIELD_REQUIRED = 'messages:FIELD_REQUIRED';

export const languagesOptions = supportedLanguages.map((key) => ({ label: key.toUpperCase(), value: key.toUpperCase() }));

export const defaultLanguageOption = getArrayElement(languagesOptions, 0);

export const commonStringifyOptions: IStringifyOptions = { skipNulls: true, addQueryPrefix: true, encode: false };

export const PLACEHOLDER = '—';
export const DEFAULT_PAGE = 0;

export const DEFAULT_DEBOUNCE_DELAY = 500;
export const DEFAULT_INPUT_DEBOUNCE = 100;

export const PERCENTAGE = {
  MIN: 0,
  MAX: 100,
} as const;

export const DROP_DOWN_ENTITY_LIMIT = 100;

export const STATUS_REQUEST_PATH = 'https://fspn8qz8zr8l.statuspage.io/api/v2/incidents.json';

export const SUPPORT_EMAIL = 'support@adconsole.com';
export const SUPPORT_YALEO_EMAIL = 'hola@yaleo.com';

export const CREATE_ACCOUNT_URL = 'https://www.audienzz.ch/create-account';

export const CURRENT_YEAR = new Date().getFullYear();

export const COOP_INVOICE_EDIT_BREAKPOINT_DAY_NUMBER = 6;

export const DEFAULT_MIN_FILE_SIZE = FileSize.Bytes(1);
export const DEFAULT_MAX_FILE_SIZE = FileSize.MiB(80);

export const CHART_LOADER_SIZE = '44px';

export enum MonthEnum {
  JANUARY = 'JANUARY',
  FEBRUARY = 'FEBRUARY',
  MARCH = 'MARCH',
  APRIL = 'APRIL',
  MAY = 'MAY',
  JUNE = 'JUNE',
  JULY = 'JULY',
  AUGUST = 'AUGUST',
  SEPTEMBER = 'SEPTEMBER',
  OCTOBER = 'OCTOBER',
  NOVEMBER = 'NOVEMBER',
  DECEMBER = 'DECEMBER',
}

export const MAX_ACCOUNT_NAME_LENGTH_COUNTRIES = ['CH', 'LI'];
